import { createContext, useContext, useState, useCallback } from 'react'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const ToastContext = createContext()

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const dismiss = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  const show = useCallback((message, tipo = 'success', duracion = 5000) => {
    const id = Date.now() + Math.random()
    setToasts(t => [...t, { id, message, tipo }])
    if (duracion > 0) setTimeout(() => dismiss(id), duracion)
    return id
  }, [dismiss])

  const toast = {
    success: (msg, d) => show(msg, 'success', d),
    error: (msg, d) => show(msg, 'error', d ?? 7000),  // errores duran más
    info: (msg, d) => show(msg, 'info', d),
    warning: (msg, d) => show(msg, 'warning', d),
    dismiss,
  }

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastContainer({ toasts, dismiss }) {
  return (
    <div style={{
      position: 'fixed',
      // iPhone/notch: el toast empieza DEBAJO del safe-area (no pegado a la hora/Dynamic Island)
      top: 'calc(env(safe-area-inset-top, 0px) + 14px)',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      pointerEvents: 'none',
      width: 'min(640px, calc((100vw / var(--z, 1)) - 20px))',
    }}>
      {toasts.map(t => <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(-22px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  )
}

// Diseño de tarjeta (mockup jul-2026): tarjeta blanca muy redondeada con canto IZQUIERDO de color,
// círculo teñido con el icono de línea (+ destello en éxito), título en color en MAYÚSCULAS,
// mensaje grande oscuro y botón × dentro de un círculo gris. Se adapta a modo oscuro por tokens.
function ToastItem({ toast, onClose }) {
  const conf = {
    success: {
      c: '#16a34a', titulo: i18nT('context.ToastContext.exito', 'Éxito'), sparkle: true,
      icono: <><circle cx="12" cy="12" r="10" /><path d="m8.4 12.3 2.4 2.4 4.8-5" /></>,
    },
    error: {
      c: '#dc2626', titulo: i18nT('context.ToastContext.error', 'Error'),
      icono: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
    },
    warning: {
      c: '#d97706', titulo: i18nT('context.ToastContext.atencion', 'Atención'),
      icono: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>,
    },
    info: {
      c: '#2563eb', titulo: i18nT('context.ToastContext.informacion', 'Información'),
      icono: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>,
    },
  }[toast.tipo] || { c: '#374151', titulo: '', icono: <circle cx="12" cy="12" r="10" /> }

  return (
    <div style={{
      background: 'var(--surface, #ffffff)',
      borderLeft: `6px solid ${conf.c}`,
      borderRadius: '22px',
      padding: '15px 16px 15px 14px',
      width: '100%',
      boxSizing: 'border-box',
      boxShadow: '0 18px 50px rgba(0,0,0,0.20), 0 4px 14px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      animation: 'toastIn 0.28s cubic-bezier(0.2, 0.9, 0.3, 1.15)',
      pointerEvents: 'auto',
    }}>
      {/* Círculo teñido con el icono de línea (+ destello arriba-dcha en éxito) */}
      <span style={{ position: 'relative', width: '54px', height: '54px', borderRadius: '50%', background: `color-mix(in srgb, ${conf.c} 11%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={conf.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{conf.icono}</svg>
        {conf.sparkle && (
          <svg width="13" height="13" viewBox="0 0 24 24" fill={conf.c} style={{ position: 'absolute', top: '1px', right: '0px' }}>
            <path d="M12 2l2.2 7.8L22 12l-7.8 2.2L12 22l-2.2-7.8L2 12l7.8-2.2z" />
          </svg>
        )}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: conf.c, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '3px' }}>{conf.titulo}</div>
        <div style={{ fontSize: '16.5px', fontWeight: 600, color: 'var(--text-primary, #111827)', lineHeight: 1.35, overflowWrap: 'anywhere' }}>{toast.message}</div>
      </div>
      <button onClick={onClose} aria-label={i18nT('context.ToastContext.cerrar', 'Cerrar')} style={{
        width: '42px', height: '42px', borderRadius: '50%', border: 'none',
        background: 'var(--surface-2, #f1f5f9)', color: 'var(--text-muted, #6b7280)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error(i18nT('context.ToastContext.usetoast_debe_usarse_dentro_de', 'useToast debe usarse dentro de <ToastProvider>'))
  return ctx
}
